// Snapshot observations are independent of the latest collection attempt.
// Never substitute impressions/reach for views, or absent counters for zero.
const ALIASES = {
  views: ['views', 'viewCount', 'viewsCount'],
  impressions: ['impressions'], reach: ['reach'],
  likes: ['likes', 'likeCount', 'likesCount'],
  reactionsTotal: ['reactionsTotal', 'reactions_total', 'reaction_count', 'reactions'],
  comments: ['comments', 'commentCount', 'replies'],
  shares: ['shares', 'shareCount'], saves: ['saves', 'saved'],
  replies: ['replies', 'comments'], reposts: ['reposts'], quotes: ['quotes'],
  total_interactions: ['total_interactions'],
};
export const METRIC_FIELDS = Object.keys(ALIASES);

// Only documented identifiers can become UI provenance. The map values are
// locale keys, never provider-supplied text or URLs.
const SOURCE_LABELS = {
  linkedin_member_post_analytics: 'sourceLinkedinAnalytics',
  linkedin_social_actions: 'sourceLinkedinActions',
  tiktok_display_api: 'sourceTikTokDisplay',
  x_public_metrics: 'sourceXPublic',
  telegram_bot_api: 'telegramOfficial',
  telegram_public_embed: 'bestEffort',
  public_embed: 'bestEffort',
};
const PROVIDER_METRIC_LABELS = {
  IMPRESSION: 'impressions', MEMBERS_REACHED: 'reach', REACTION: 'reactionsTotal',
  COMMENT: 'comments', RESHARE: 'shares', POST_SAVE: 'saves',
  post_media_view: 'mediaViews', post_total_media_view_unique: 'uniqueMediaViews',
  'likes.summary.total_count': 'likesSummary',
  views: 'views', reach: 'reach', likes: 'likes', comments: 'comments',
  saved: 'saves', saves: 'saves', shares: 'shares', replies: 'replies',
  reposts: 'reposts', quotes: 'quotes', total_interactions: 'total_interactions',
  view_count: 'views', like_count: 'likes', comment_count: 'comments',
  share_count: 'shares', impression_count: 'impressions',
};
function vettedIdentifier(labels, value) {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(labels, value) ? value : null;
}

export function metricNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

export function formatMetric(value, locale) {
  const n = metricNumber(value);
  return n === null ? '—' : new Intl.NumberFormat(locale || 'en', {
    notation: n >= 1000 ? 'compact' : 'standard', maximumFractionDigits: 1,
  }).format(n);
}

export function normalizeMetrics(source, metricFetch) {
  const raw = source || {};
  const out = { ...raw, metricFetch: metricFetch || raw.metricFetch };
  for (const [field, aliases] of Object.entries(ALIASES)) {
    // An explicit null in the canonical field must survive legacy aliases.
    const key = aliases.find(key => Object.prototype.hasOwnProperty.call(raw, key));
    let value = key ? raw[key] : null;
    if (field === 'reactionsTotal' && value && typeof value === 'object') value = value.total ?? value.count;
    const availability = aliases.map(key => raw.metricAvailability?.[key]).find(Boolean);
    out[field] = availability?.available === false ? null : metricNumber(value);
  }
  if (raw.viewsAvailable === false && raw.metricAvailability?.views?.available !== true) out.views = null;
  out.viewsAvailable = out.views !== null;
  out.viewsPending = false;
  out.available = METRIC_FIELDS.some(field => out[field] !== null);
  return out;
}

export function metricReasonKey(reason) {
  const code = typeof reason === 'string' ? reason.toLowerCase() : '';
  if (['authentication_failed', 'missing_account_or_token', 'account_revoked', 'token_expired'].includes(code)) return 'authentication';
  if (['permission_denied', 'scope_required', 'read_scope_required', 'metrics_scope_not_granted', 'metrics_wrong_account'].includes(code)) return 'permission';
  if (['rate_limited', 'quota_limited', 'active_cooldown', 'recent_attempt'].includes(code)) return 'limited';
  if (['unsupported_platform', 'unsupported_metric', 'unsupported_account_type'].includes(code)) return 'unsupported';
  if (code === 'telegram_public_channel_required') return 'publicChannel';
  if (['read_budget_required', 'read_budget_disabled', 'read_budget_exhausted'].includes(code)) return 'readBudget';
  if (code === 'processing_pending') return 'processing';
  return 'unavailable';
}

export function metricErrorReason(error) {
  const code = error?.details?.reason || error?.code;
  if (code && metricReasonKey(code) !== 'unavailable') return code;
  if (error?.status === 401) return 'authentication_failed';
  if (error?.status === 403) return 'permission_denied';
  if (error?.status === 429) return 'rate_limited';
  return 'metrics_unavailable';
}

export function readMetric(source, field) {
  const metrics = normalizeMetrics(source);
  const aliases = ALIASES[field] || [field];
  const availability = aliases.map(key => metrics.metricAvailability?.[key]).find(Boolean) || {};
  const value = availability.available === false ? null : metricNumber(metrics[field]);
  const measuredAt = Object.prototype.hasOwnProperty.call(availability, 'measuredAt')
    ? availability.measuredAt : metrics.legacyFetchedAt || metrics.fetchedAt || null;
  const refresh = metrics.metricRefresh || {};
  const missing = aliases.some(key => refresh.unavailableFields?.includes(key))
    && !(Date.parse(measuredAt) > Date.parse(refresh.attemptedAt));
  const fieldReason = aliases.map(key => refresh.unavailableReasons?.[key]).find(Boolean);
  const fetch = metrics.metricFetch || {};
  const attemptAfterObservation = !measuredAt || Date.parse(fetch.lastAttemptedAt) > Date.parse(measuredAt);
  const failedAttempt = ['failed', 'skipped'].includes(fetch.state) && attemptAfterObservation;
  // Per-field official metadata wins over unrelated public-embed history.
  const hasFieldSource = Object.prototype.hasOwnProperty.call(availability, 'source');
  const sourceName = hasFieldSource ? availability.source : metrics.metricSource;
  const observedSource = vettedIdentifier(SOURCE_LABELS, sourceName);
  const providerMetric = vettedIdentifier(PROVIDER_METRIC_LABELS, availability.providerMetric);
  return {
    value, measuredAt,
    source: observedSource, providerMetric,
    sourceLabelKey: observedSource ? SOURCE_LABELS[observedSource] : null,
    providerMetricLabelKey: providerMetric ? PROVIDER_METRIC_LABELS[providerMetric] : null,
    stale: value !== null && (availability.stale === true || missing || failedAttempt),
    partial: availability.partial === true,
    bestEffort: value !== null && (availability.bestEffort === true || (!hasFieldSource && metrics.bestEffort === true)
      || sourceName === 'telegram_public_embed' || sourceName === 'public_embed'),
    officialTelegram: value !== null && sourceName === 'telegram_bot_api',
    reason: fieldReason || availability.reason || (missing ? 'metrics_unavailable' : fetch.reason),
  };
}

// A live panel may retain its own last observation for the SAME publication.
// Missing fields never inherit the new attempt's timestamp or provenance.
export function retainLiveMetrics(previous, incoming) {
  const next = normalizeMetrics(incoming);
  if (!previous) return next;
  const result = { ...next, metricAvailability: { ...next.metricAvailability },
    metricRefresh: { ...next.metricRefresh, unavailableFields: [...(next.metricRefresh?.unavailableFields || [])] } };
  for (const field of METRIC_FIELDS) {
    const old = readMetric(previous, field);
    if (readMetric(next, field).value !== null || old.value === null) continue;
    result[field] = old.value;
    result.metricAvailability[field] = {
      available: true, measuredAt: old.measuredAt, stale: true,
      partial: old.partial, bestEffort: old.bestEffort,
      source: old.source, providerMetric: old.providerMetric,
    };
    result.metricRefresh.unavailableFields.push(field);
  }
  return normalizeMetrics(result);
}

// Sum only observed values; a mixed sum is explicitly partial. An empty set is
// zero only when the caller knows that there were no posts in its scope.
export function aggregateMetrics(sources, { emptyIsZero = false, partial = false } = {}) {
  const result = { metricAvailability: {} };
  for (const field of METRIC_FIELDS) {
    const readings = sources.map(source => readMetric(source, field));
    const known = readings.filter(reading => reading.value !== null);
    result[field] = known.length ? known.reduce((sum, reading) => sum + reading.value, 0)
      : sources.length === 0 && emptyIsZero ? 0 : null;
    const times = known.map(reading => reading.measuredAt).filter(time => Number.isFinite(Date.parse(time))).sort((a, b) => Date.parse(a) - Date.parse(b));
    result.metricAvailability[field] = {
      available: result[field] !== null,
      measuredAt: times[0] || null,
      partial: partial || known.length < readings.length || known.some(reading => reading.partial),
      stale: known.some(reading => reading.stale),
      bestEffort: known.some(reading => reading.bestEffort),
    };
  }
  return normalizeMetrics(result);
}

export function aggregateMetricSeries(rows) {
  const count = row => metricNumber(row.published ?? row.postCount ?? row.posts);
  const publications = rows.filter(row => count(row) !== 0);
  const result = aggregateMetrics(publications, { emptyIsZero: rows.length > 0 && publications.length === 0 });
  for (const field of METRIC_FIELDS) {
    // Older daily payloads omit post counts. In an incomplete all-zero series,
    // those zeros may ALL be empty-day buckets, not observations of posts.
    // Withhold that ambiguous total, while preserving each chart point.
    if (result[field] === 0 && result.metricAvailability[field].partial
      && !publications.some(row => readMetric(row, field).value !== null
        && (count(row) > 0 || row.metricAvailability?.[field]?.available === true))) {
      result[field] = null;
      result.metricAvailability[field].available = false;
    }
  }
  return normalizeMetrics(result);
}

export function engagementRate(source) {
  const metrics = normalizeMetrics(source);
  const fields = [reactionField(metrics), 'comments', 'shares', 'saves'];
  const values = fields.map(field => readMetric(metrics, field));
  const views = readMetric(metrics, 'views');
  if (!(views.value > 0) || views.partial || views.stale
    || values.some(reading => reading.value === null || reading.partial || reading.stale)) return null;
  return values.reduce((sum, reading) => sum + reading.value, 0) / views.value * 100;
}

export function reactionField(metrics, platform) {
  return readMetric(metrics, 'reactionsTotal').value !== null || ['telegram', 'linkedin'].includes(platform)
    ? 'reactionsTotal' : 'likes';
}

export function nullableSum(values) {
  const known = values.map(metricNumber).filter(value => value !== null);
  return known.length ? known.reduce((sum, value) => sum + value, 0) : values.length ? null : 0;
}

// Separate paths across unknown days, including isolated measured-zero points.
export function metricSegments(data, field, xy) {
  const segments = [];
  let current = [];
  data.forEach((row, index) => {
    if (metricNumber(row[field]) === null) {
      if (current.length) segments.push(current);
      current = [];
    } else current.push(xy(index, row[field]));
  });
  if (current.length) segments.push(current);
  return segments;
}

export function refreshOutcome(result) {
  const data = result?.details?.metricRefresh || result?.data || result;
  const results = data?.engagement?.data?.results;
  const recentSkipReasons = new Set(['active_cooldown', 'recent_attempt', 'quota_limited', 'claim_in_progress', 'provider_backoff']);
  const deferred = data?.refreshed !== true
    && Array.isArray(results)
    && results.length > 0
    && results.every(item => item?.status === 'skipped' && recentSkipReasons.has(item?.reason));
  const retryTimes = deferred ? results.map(item => typeof item.retryAt === 'string' ? Date.parse(item.retryAt) : NaN) : null;
  const retryAt = retryTimes?.every(Number.isFinite) ? new Date(Math.max(...retryTimes)).toISOString() : null;
  return { refreshed: data?.refreshed === true, complete: data?.complete === true, deferred, retryAt };
}
